import { type MouseEvent } from 'react';

import { useHiveRenderingMetadata } from './use-hive-rendering-metadata.js';
import { useHiveTileBestFit } from './use-hive-tile-best-fit.js';
import { useHoneycombData } from './use-honeycomb-data.js';
import { useHoneycombShape } from './use-honeycomb-shape.js';
import { useHoneycombTooltipMetadata } from './use-honeycomb-tooltip-metadata.js';
import type { Overlay } from '../../core/components/overlay-tooltip/types.js';
import { useOverlayEventHandlers } from '../../core/hooks/useOverlayEventHandlers.js';
import { useSetStateOverlay } from '../store/store.js';
import { buildOverlayTooltipData } from '../utils/build-overlay-tooltip-data.js';
import { findClosestTileStrategy } from '../utils/find-closest-tile.js';

export const useOverlayEvents = (overlay: Overlay) => {
  const setOverlayState = useSetStateOverlay();
  const honeycombData = useHoneycombData();
  const { tilesCenters } = useHiveRenderingMetadata();
  const { tileWidth } = useHiveTileBestFit();
  const shape = useHoneycombShape();
  const { tooltipMetadata } = useHoneycombTooltipMetadata();

  const getTooltipDataFromEvent = (event: MouseEvent<HTMLDivElement>) => {
    const position = overlay.getAdjustedPositionsFromAbsolute(
      event.clientX,
      event.clientY,
    );
    const relativePosition = {
      x: position.relativeX ?? 0,
      y: position.relativeY ?? 0,
    };
    const closestTile = findClosestTileStrategy[shape](
      tileWidth,
      tilesCenters,
      relativePosition,
    );
    return closestTile !== undefined
      ? buildOverlayTooltipData(closestTile, honeycombData, tooltipMetadata)
      : undefined;
  };

  return useOverlayEventHandlers(overlay, {
    onToolbarVisibilityChange: (visible) =>
      setOverlayState({ toolbarVisible: visible }),
    getTooltipData: getTooltipDataFromEvent,
  });
};
